import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;
  #summaryEvents = null;
  #removing = false;

  connectedCallback() {
    super.connectedCallback();
    this.#summaryEvents?.abort();
    this.#summaryEvents = new AbortController();
    this.closest('.cart-drawer__summary')?.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-dj-remove-discount]')) {
        this.removeDiscount(event);
      }
    }, { signal: this.#summaryEvents.signal });
  }

  disconnectedCallback() {
    this.#summaryEvents?.abort();
    super.disconnectedCallback();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.#removing) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const existingDiscounts = this.#existingDiscounts();
    const abortController = this.#createAbortController();

    try {
      const normalizedDiscountCode = this.#normalizeCode(discountCodeValue);
      if (existingDiscounts.some((code) => this.#normalizeCode(code) === normalizedDiscountCode)) return;

      this.#clearDiscountErrors();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`Discount update failed with status ${response.status}`);

      const data = await response.json();
      const returnedDiscounts = Array.isArray(data?.discount_codes) ? data.discount_codes : [];
      const returnedDiscount = returnedDiscounts.find(
        (/** @type {{ code?: unknown; applicable?: unknown }} */ discount) =>
          typeof discount?.code === 'string' && this.#normalizeCode(discount.code) === normalizedDiscountCode
      );
      const existingDiscountRejected = existingDiscounts.some((existingDiscount) => {
        const normalizedExistingDiscount = this.#normalizeCode(existingDiscount);
        const returnedExistingDiscount = returnedDiscounts.find(
          (/** @type {{ code?: unknown; applicable?: unknown }} */ discount) =>
            typeof discount?.code === 'string' &&
            this.#normalizeCode(discount.code) === normalizedExistingDiscount
        );
        return returnedExistingDiscount?.applicable !== true;
      });

      // Shopify is the only source of truth. A discount update can return HTTP 200 while
      // one or more submitted codes are rejected. The requested combination is atomic:
      // unless Shopify accepts every code, restore the previously accepted set and fail closed.
      if (returnedDiscount?.applicable !== true || existingDiscountRejected) {
        await this.#restoreDiscounts(existingDiscounts, abortController.signal);
        discountCode.value = '';
        this.#handleDiscountError(existingDiscounts.length > 0 ? 'combination' : 'discount_code');
        return;
      }

      const newHtml = data?.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || newHtml.length === 0) {
        throw new Error('Discount update did not return the cart section');
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const normalizedExistingDiscounts = existingDiscounts.map((code) => this.#normalizeCode(code));
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter((code) => typeof code === 'string')
          .map((code) => this.#normalizeCode(code));

        // Before morphing, we need to check if the shipping discount is applicable in the UI.
        // We compare Liquid's allocation-derived pills with the cart payload so a shipping-only
        // code is never presented as a monetary cart saving before checkout can resolve shipping.
        if (
          codes.length === normalizedExistingDiscounts.length &&
          codes.every((code) => normalizedExistingDiscounts.includes(code))
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      if (!this.#morphOpenDrawer(parsedHtml)) morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        try {
          await this.#restoreDiscounts(existingDiscounts, abortController.signal);
        } catch (restoreError) {
        }
        this.#handleDiscountError('request');
      }
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Updates only the stable cart regions while the drawer is already modal.
   * Replacing the complete Header section would remove the open dialog from the
   * top layer and make the drawer visibly close and reopen.
   * @param {Document} parsedHtml
   * @returns {boolean}
   */
  #morphOpenDrawer(parsedHtml) {
    const currentDialog = this.closest('dialog');
    if (
      !(currentDialog instanceof HTMLDialogElement) ||
      !currentDialog.open ||
      !currentDialog.matches(':modal') ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return false;
    }

    const nextSection = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`) || parsedHtml;
    const nextDialog =
      nextSection.querySelector('cart-drawer-component .cart-drawer__dialog') ||
      nextSection.querySelector('.cart-drawer__dialog');
    const currentCartItems = currentDialog.querySelector('cart-items-component');
    const nextCartItems = nextDialog?.querySelector('cart-items-component');
    if (!(currentCartItems instanceof HTMLElement) || !(nextCartItems instanceof HTMLElement)) return false;

    const regionSelectors = [
      '.cart-drawer__header',
      '.dj-cart-top',
      '.cart-items__wrapper',
      '.cart-drawer__summary',
    ];
    const regions = regionSelectors.map((selector) => ({
      current: currentCartItems.querySelector(selector),
      next: nextCartItems.querySelector(selector),
    }));

    const scrollContainer = currentDialog.querySelector('.cart-drawer__items');
    const scrollTop = scrollContainer?.scrollTop || 0;

    if (regions.every(({ current, next }) => current && next)) {
      for (const { current, next } of regions) {
        if (!current || !next) continue;
        try {
          morph(current, next);
        } catch {
          current.replaceWith(next);
        }
      }
    } else {
      const currentRecommendations = currentCartItems.querySelector('dj-cart-recommendations');
      const nextRecommendations = nextCartItems.querySelector('dj-cart-recommendations');
      try {
        currentRecommendations?.setAttribute('data-skip-subtree-update', '');
        nextRecommendations?.setAttribute('data-skip-subtree-update', '');
        morph(currentCartItems, nextCartItems);
      } finally {
        currentRecommendations?.removeAttribute('data-skip-subtree-update');
      }
    }

    /** @type {any} */ (currentCartItems).updatedCallback?.();

    const note = currentDialog.querySelector('cart-note');
    const noteDetails = note?.querySelector('details');
    if (noteDetails instanceof HTMLDetailsElement && note?.dataset.djNoteEditing !== 'true') noteDetails.open = false;

    const discountDisclosure = currentDialog.querySelector('disclosure-custom.cart-discount');
    const discountTrigger = discountDisclosure?.querySelector('.disclosure-trigger');
    const discountContent = discountDisclosure?.querySelector('.disclosure-content');
    if (discountTrigger instanceof HTMLButtonElement) {
      discountTrigger.setAttribute('aria-expanded', 'false');
      if (discountTrigger.dataset.disclosureOpen) {
        discountTrigger.setAttribute('aria-label', discountTrigger.dataset.disclosureOpen);
      }
    }
    if (discountContent instanceof HTMLElement) discountContent.setAttribute('inert', '');

    currentDialog.querySelector('dj-cart-reservation')?.sync?.();
    window.requestAnimationFrame(() => {
      const nextScrollContainer = currentDialog.querySelector('.cart-drawer__items');
      if (nextScrollContainer instanceof HTMLElement) nextScrollContainer.scrollTop = scrollTop;
      const nextDiscountTrigger = currentDialog.querySelector(
        'disclosure-custom.cart-discount .disclosure-trigger'
      );
      if (nextDiscountTrigger instanceof HTMLButtonElement) {
        nextDiscountTrigger.focus({ preventScroll: true });
      }
    });
    return true;
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      this.#removing ||
      !(event.target instanceof Element) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    // Both the existing disclosure X and the compact summary use this same path.
    const summaryButton = event.target.closest('[data-dj-remove-discount]');
    const pill = event.target.closest('.cart-discount__pill');
    const discountCode = summaryButton?.dataset.djRemoveDiscount || pill?.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.findIndex((code) => this.#normalizeCode(code) === this.#normalizeCode(discountCode));
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const button = summaryButton || event.target.closest('button');
    const summary = this.closest('.cart-drawer__summary');
    const feedback = summary?.querySelector('[data-dj-discount-remove-error]');
    if (feedback) feedback.textContent = '';
    this.#removing = true;
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    const timeout = setTimeout(() => abortController.abort(), 8000);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('Discount removal failed');
      const data = await response.json();
      const accepted = Array.isArray(data?.discount_codes)
        ? data.discount_codes.filter((entry) => entry?.applicable === true && typeof entry.code === 'string')
          .map((entry) => this.#normalizeCode(entry.code))
        : null;
      if (!accepted || data.discount_codes.some((entry) => typeof entry?.code === 'string' &&
          this.#normalizeCode(entry.code) === this.#normalizeCode(discountCode)) ||
          existingDiscounts.some((code) => !accepted.includes(this.#normalizeCode(code)))) {
        throw new Error('Discount removal was not confirmed by Shopify');
      }

      const newHtml = data?.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string' || newHtml.length === 0) {
        throw new Error('Discount removal did not return the cart section');
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      if (!this.#morphOpenDrawer(parsedHtml)) morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      if (feedback?.isConnected) {
        feedback.textContent = 'Rabatten kunde inte uppdateras. Försök igen eller ladda om varukorgen.';
      } else {
        this.#handleDiscountError('request');
      }
    } finally {
      clearTimeout(timeout);
      this.#removing = false;
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  #clearDiscountErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    const input = this.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement) input.removeAttribute('aria-invalid');
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'combination' | 'request' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const isShipping = type === 'shipping';
    const target = isShipping ? cartDiscountErrorShipping : cartDiscountErrorDiscountCode;

    this.#clearDiscountErrors();

    if (!isShipping) {
      if (type === 'combination' && this.dataset.discountCombinationError) {
        target.textContent = this.dataset.discountCombinationError;
      } else if (type === 'request' && this.dataset.discountRequestError) {
        target.textContent = this.dataset.discountRequestError;
      } else if (target.dataset.defaultError) {
        target.textContent = target.dataset.defaultError;
      }
    }

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
    const input = this.querySelector('input[name="discount"]');
    if (input instanceof HTMLInputElement) input.setAttribute('aria-invalid', 'true');
  }

  /**
   * Restores the previously accepted discount-code set after Shopify rejects a request.
   * This removes inapplicable attempted codes from the server cart without morphing stale UI.
   * @param {string[]} discountCodes
   * @param {AbortSignal} signal
   */
  async #restoreDiscounts(discountCodes, signal) {
    const config = fetchConfig('json', {
      body: JSON.stringify({ discount: discountCodes.join(',') }),
    });
    const response = await fetch(Theme.routes.cart_update_url, { ...config, signal });
    if (!response.ok) throw new Error(`Discount rollback failed with status ${response.status}`);
    await response.json();
  }

  /**
   * Normalizes a code for comparison while preserving the submitted form for Shopify.
   * Shopify discount codes are case-insensitive.
   * @param {string} code
   * @returns {string}
   */
  #normalizeCode(code) {
    return code.trim().toLocaleUpperCase('en-US');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
